import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Home, BookOpen, Trophy, User, Users, LogOut, Bell, Gift, FileText, HelpCircle, Video, Target, Settings, ChevronDown, ChevronRight, FolderOpen, Download, Menu, X, Database, Webhook } from 'lucide-react';

export default function Layout({ children, user, onLogout, currentPage }) {
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [currentPage]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const studentMenu = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/' },
    { id: 'profile', label: 'My Profile', icon: User, path: '/profile' },
    { id: 'courses', label: 'My Courses', icon: BookOpen, path: '/courses' },
    { id: 'live-classes', label: 'Live Classes', icon: Video, path: '/live-classes' },
    { id: 'leaderboard', label: 'Leaderboard', icon: Trophy, path: '/leaderboard' },
    { id: 'announcements', label: 'Announcements', icon: Bell, path: '/announcements' },
    { id: 'community', label: 'Community', icon: Users, path: '/community' },
    { id: 'help', label: 'Mentors', icon: HelpCircle, path: '/mentors' },
    { id: 'challenges', label: 'Challenges', icon: Target, path: '/challenges' },
    { id: 'rewards', label: 'Rewards', icon: Gift, path: '/rewards' }
  ];

  const teacherMenu = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/' },
    { id: 'courses', label: 'My Courses', icon: BookOpen, path: '/courses' },
    { id: 'challenges', label: 'Challenges', icon: Target, path: '/challenge-management' },
    { id: 'mentors', label: 'Mentors', icon: Users, path: '/mentors' },
    { id: 'live-classes', label: 'Live Classes', icon: Video, path: '/live-classes' },
    { id: 'announcements', label: 'Announcements', icon: Bell, path: '/announcements' },
    { id: 'community', label: 'Community', icon: Users, path: '/community' },
    { id: 'doubts-resolution', label: 'Doubts Resolution', icon: HelpCircle, path: '/doubts-resolution' },
    { id: 'teacher-reports', label: 'Reports', icon: FileText, path: '/teacher-reports' },
    { id: 'teacher-profile', label: 'My Profile', icon: User, path: '/teacher-profile' }
  ];

  const adminMenu = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/' },
    { id: 'courses', label: 'My Courses', icon: BookOpen, path: '/courses' },
    { id: 'challenges', label: 'Challenges', icon: Target, path: '/challenge-management' },
    { 
      id: 'user-management', 
      label: 'User Management', 
      icon: FolderOpen, 
      isParent: true,
      children: [
        { id: 'users', label: 'Add Users', icon: Users, path: '/users' },
        { id: 'custom-fields', label: 'Custom Fields', icon: Settings, path: '/custom-fields' },
        { id: 'download-reports', label: 'Download Reports', icon: Download, path: '/download-reports' }
      ]
    },
    { id: 'mentors', label: 'Mentors', icon: Users, path: '/mentors' },
    { id: 'polls', label: 'Polls', icon: FileText, path: '/admin/polls' },
    { id: 'live-classes', label: 'Live Classes', icon: Video, path: '/live-classes' },
    { id: 'announcements', label: 'Announcements', icon: Bell, path: '/announcements' },
    { id: 'community', label: 'Community', icon: Users, path: '/community' },
    { id: 'backup-management', label: 'Backup Management', icon: Database, path: '/backup-management' },
    { id: 'webhook-manager', label: 'Webhook Manager', icon: Webhook, path: '/webhook-manager' },
    { id: 'activity-log', label: 'Activity Log', icon: FileText, path: '/activity-log' },
    { id: 'admin-profile', label: 'My Profile', icon: User, path: '/admin-profile' }
  ];

  const mentorMenu = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/' },
    { id: 'profile', label: 'My Profile', icon: User, path: '/profile' }
  ];

  const getMenuForRole = () => {
    switch(user.role) {
      case 'student': return studentMenu;
      case 'teacher': return teacherMenu;
      case 'mentor': return mentorMenu;
      case 'admin': return adminMenu;
      default: return mentorMenu;
    }
  };

  const menu = getMenuForRole();

  const handleNavigation = (item) => {
    if (item.comingSoon) return;
    if (item.isParent) {
      toggleMenu(item.id);
    } else {
      navigate(item.path);
    }
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const isChildActive = (item) => {
    if (!item.children) return false;
    return item.children.some(child => child.id === currentPage);
  };

  return (
    <div className="min-h-screen flex" data-testid="layout">
      {/* Mobile Header with Hamburger */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E7E7E7] px-4 py-3 flex items-center justify-between">
        <img 
          src="/gdiot-logo-new.png"
          alt="GDIOT Logo"
          className="h-10 w-auto object-contain"
        />
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <X className="w-6 h-6 text-navy" strokeWidth={2} />
          ) : (
            <Menu className="w-6 h-6 text-navy" strokeWidth={2} />
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop & Mobile Slide-in */}
      <div className={`
        fixed md:static inset-y-0 left-0 z-40
        w-64 border-r border-[#E7E7E7] flex flex-col bg-white
        transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Logo - Hidden on mobile (shown in mobile header) */}
        <div className="hidden md:flex p-6 border-b border-[#E7E7E7] items-center justify-center">
          <img 
            src="/gdiot-logo-new.png"
            alt="GDIOT Logo"
            className="h-14 w-auto object-contain"
          />
        </div>

        {/* Mobile: Close button inside sidebar */}
        <div className="md:hidden p-4 border-b border-[#E7E7E7] flex items-center justify-between">
          <img 
            src="/gdiot-logo-new.png"
            alt="GDIOT Logo"
            className="h-10 w-auto object-contain"
          />
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-navy" strokeWidth={2} />
          </button>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-1">
            {menu.map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.id;
              const isExpanded = expandedMenus[item.id];
              const hasActiveChild = isChildActive(item);
              
              return (
                <div key={item.id}>
                  {/* Parent Menu Item */}
                  <button
                    onClick={() => handleNavigation(item)}
                    disabled={item.comingSoon}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-smooth ${
                      isActive || hasActiveChild
                        ? 'font-semibold bg-primary-blue text-white'
                        : 'text-[#4D4D4D] hover:bg-[#F7F7F7]'
                    } ${item.comingSoon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`nav-${item.id}`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={isActive || hasActiveChild ? 2.5 : 1.5} />
                    <span className="text-[15px] font-medium flex-1 text-left">{item.label}</span>
                    {item.comingSoon && (
                      <span className="ml-auto text-xs px-2 py-1 rounded" style={{ background: '#ffe8d6', color: '#d86a2e' }}>
                        Soon
                      </span>
                    )}
                    {item.isParent && (
                      isExpanded ? 
                        <ChevronDown className="w-4 h-4 ml-auto" /> : 
                        <ChevronRight className="w-4 h-4 ml-auto" />
                    )}
                  </button>

                  {/* Child Menu Items */}
                  {item.isParent && isExpanded && item.children && (
                    <div className="mt-1 ml-4 space-y-1">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const isChildActive = currentPage === child.id;
                        return (
                          <button
                            key={child.id}
                            onClick={() => navigate(child.path)}
                            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-smooth ${
                              isChildActive
                                ? 'font-semibold bg-blue-50 text-primary-blue border-l-2 border-primary-blue'
                                : 'text-[#4D4D4D] hover:bg-[#F7F7F7]'
                            }`}
                            data-testid={`nav-${child.id}`}
                          >
                            <ChildIcon className="w-4 h-4" strokeWidth={isChildActive ? 2.5 : 1.5} />
                            <span className="text-[14px] font-medium">{child.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        <div className="p-4 border-t border-[#E7E7E7]">
          <button
            onClick={onLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-smooth hover:bg-[#FFF5F5] text-[#DC2626] font-medium"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[15px]">Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content - Add top padding on mobile for fixed header */}
      <div className="flex-1 overflow-y-auto pt-16 md:pt-0" style={{ background: '#fef7f0' }}>
        {children}
      </div>
    </div>
  );
}